<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useDateTime } from "@/composables";
import { MINUTE_IN_MILLISECONDS } from "@/constants/time";
import dayjs from "dayjs";

const { formatTimeDiff } = useDateTime();

const props = withDefaults(
  defineProps<{
    truckNumber: string | undefined;
    interval: number | null;
    nextUpdateTime: string | null;
    truckId: number | undefined;
    disabled?: boolean;
  }>(),
  {
    truckNumber: "",
    truckId: undefined,
    interval: null,
    disabled: false,
  },
);

const emits = defineEmits<{
  (e: "openDialog"): void;
}>();

const remainingTime = ref<string>("");
const intervalId = ref<ReturnType<typeof setInterval> | null>(null);

const isExpired = computed(() => {
  if (!props.nextUpdateTime) {
    return false;
  }

  return dayjs.utc().isAfter(dayjs.utc(props.nextUpdateTime));
});

function getTimerClass(disabled: boolean) {
  if (disabled || !isExpired.value) {
    return "bg-blue-grey-lighten-5 border-blue-grey-darken-1";
  }

  return "bg-red-lighten-5 border-danger";
}

function getTimerTextClass(disabled: boolean) {
  if (disabled || !isExpired.value) {
    return "text-blue-grey-darken-1";
  }
  return "text-danger";
}

function setRemainingTime() {
  if (!props.nextUpdateTime) {
    remainingTime.value = "Not scheduled";

    return;
  }

  return (remainingTime.value = formatTimeDiff(props.nextUpdateTime));
}

function setRemainingTimeInterval() {
  intervalId.value = setInterval(() => {
    setRemainingTime();
  }, MINUTE_IN_MILLISECONDS);
}

function openDialog() {
  emits("openDialog");
}

watch(
  () => [props.interval, props.nextUpdateTime],
  () => {
    setRemainingTime();
  },
);

onMounted(() => {
  setRemainingTime();
  setRemainingTimeInterval();
});

onUnmounted(() => {
  if (intervalId.value) {
    clearInterval(intervalId.value);
  }

  intervalId.value = null;
});
</script>

<template>
  <v-btn
    :class="getTimerClass(disabled)"
    :disabled="disabled"
    data-qa="timer-button"
    density="comfortable"
    size="small"
    variant="outlined"
    @click="openDialog"
  >
    <v-icon :class="getTimerTextClass(disabled)" class="mr-1" icon="mdi-timer-outline" />
    <span :class="getTimerTextClass(disabled)" class="font-size-11">{{ remainingTime }}</span>
  </v-btn>
</template>

<style lang="scss" scoped></style>
